/**
 * Alumni assigned-task form — professional, one-take lock.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';

const MOODS = [
  { value: 1, label: 'Poor' },
  { value: 2, label: 'Fair' },
  { value: 3, label: 'Good' },
  { value: 4, label: 'Very good' },
  { value: 5, label: 'Excellent' },
];

export default function AlumniFeedbackForm() {
  const [params] = useSearchParams();
  const taskId = params.get('taskId');
  const [detail, setDetail] = useState(null);
  const [step, setStep] = useState(0);
  const [ratings, setRatings] = useState({});
  const [strengths, setStrengths] = useState('');
  const [improvements, setImprovements] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const prompts = detail?.prompts || [];
  const current = prompts[step];
  const progress = useMemo(
    () => (prompts.length ? ((step + 1) / (prompts.length + 1)) * 100 : 0),
    [step, prompts.length]
  );

  useEffect(() => {
    if (!taskId) {
      setError('Missing task. Open Submit feedback from the alumni dashboard.');
      setLoading(false);
      return;
    }
    api
      .get(`/alumni-feedback/tasks/${taskId}`)
      .then((res) => {
        const data = res.data?.data;
        setDetail(data);
        if (data?.submission) setDone(true);
      })
      .catch((err) => setError(err.response?.data?.error?.message || 'Could not load this task.'))
      .finally(() => setLoading(false));
  }, [taskId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payloadRatings = prompts.map((item) => ({
      key: item.key,
      rating: Number(ratings[item.key]?.rating) || 0,
      comments: (ratings[item.key]?.comments || '').trim() || undefined,
    }));
    if (payloadRatings.some((item) => item.rating < 1)) {
      setError('Rate every prompt before sending.');
      setStep(payloadRatings.findIndex((item) => item.rating < 1));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/alumni-feedback/submit', {
        taskId,
        ratings: payloadRatings,
        strengths: strengths.trim() || undefined,
        improvements: improvements.trim() || undefined,
        additionalComments: additionalComments.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not submit alumni feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  const task = detail?.task;

  return (
    <Layout>
      <div className="alu-form-page">
        <div className="genz-inner">
          <p className="text-xs uppercase tracking-[0.2em] text-amber-200/80 mb-2">Alumni feedback · One take</p>
          <h1 className="text-3xl font-semibold text-white m-0">{task?.title || 'Assigned feedback'}</h1>
          <p className="mt-2 text-slate-300">{task?.description || 'Share a professional view. After submit, this response is locked.'}</p>
          <p className="genz-lock mt-3">Admin assigned this task to you. It cannot be edited after send.</p>

          <div className="genz-card mt-6">
            {loading ? <p>Loading task…</p> : null}
            {error ? <div className="alert-error">{error}</div> : null}
            {done ? (
              <div className="text-center py-8 space-y-3">
                <p className="text-5xl">🎓</p>
                <h2 className="text-2xl font-semibold text-white m-0">Locked in</h2>
                <p className="text-slate-300">Thank you. Your alumni feedback is saved and cannot be changed.</p>
                <button type="button" className="genz-submit" onClick={() => window.close()}>Close this tab</button>
              </div>
            ) : !loading && task ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <div className="flex justify-between text-xs text-slate-300 mb-2">
                    <span>{prompts.filter((item) => Number(ratings[item.key]?.rating) >= 1).length}/{prompts.length} rated</span>
                    <span>{step < prompts.length ? `Prompt ${step + 1} of ${prompts.length}` : 'Final notes'}</span>
                  </div>
                  <div className="genz-progress"><span style={{ width: `${progress}%` }} /></div>
                </div>

                {step < prompts.length && current ? (
                  <div className="genz-criterion">
                    <p className="text-lg font-semibold text-white m-0">{current.label}</p>
                    <p className="text-sm text-slate-300 m-0">{current.hint}</p>
                    <div className="genz-rate">
                      {MOODS.map((mood) => (
                        <button
                          key={mood.value}
                          type="button"
                          className={Number(ratings[current.key]?.rating) === mood.value ? 'selected' : ''}
                          onClick={() => setRatings((prev) => ({ ...prev, [current.key]: { ...prev[current.key], rating: mood.value } }))}
                        >
                          {mood.value}<span className="block text-xs mt-1">{mood.label}</span>
                        </button>
                      ))}
                    </div>
                    <input
                      className="genz-input"
                      maxLength={500}
                      placeholder="Optional comment"
                      value={ratings[current.key]?.comments || ''}
                      onChange={(e) => setRatings((prev) => ({ ...prev, [current.key]: { ...prev[current.key], comments: e.target.value } }))}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <label className="block text-sm text-slate-200">What worked well<textarea className="genz-input mt-2" rows={2} value={strengths} onChange={(e) => setStrengths(e.target.value)} /></label>
                    <label className="block text-sm text-slate-200">What should improve<textarea className="genz-input mt-2" rows={2} value={improvements} onChange={(e) => setImprovements(e.target.value)} /></label>
                    <label className="block text-sm text-slate-200">Anything else<textarea className="genz-input mt-2" rows={2} value={additionalComments} onChange={(e) => setAdditionalComments(e.target.value)} /></label>
                  </div>
                )}

                <div className="flex gap-3">
                  {step > 0 ? <button type="button" className="btn-secondary" onClick={() => setStep((n) => n - 1)}>Back</button> : null}
                  {step < prompts.length ? (
                    <button
                      type="button"
                      className="genz-submit"
                      onClick={() => {
                        if (Number(ratings[current.key]?.rating) < 1) {
                          setError('Choose a rating to continue.');
                          return;
                        }
                        setError('');
                        setStep((n) => n + 1);
                      }}
                    >
                      Next
                    </button>
                  ) : (
                    <button type="submit" disabled={submitting} className="genz-submit">
                      {submitting ? 'Sending…' : 'Submit & lock'}
                    </button>
                  )}
                </div>
              </form>
            ) : null}
          </div>
        </div>
      </div>
    </Layout>
  );
}
